import {
  faCaretDown, faCaretUp,
} from '@fortawesome/pro-solid-svg-icons';
import type { OverlayScrollbarsComponentRef } from 'overlayscrollbars-react';
import {
  type FC, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { DividerComponent } from '~/_shared/baseComponents/divider';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import {
  TooltipComponent, TooltipPlacement,
} from '~/_shared/baseComponents/tooltip/tooltip.component';
import { OverlayScrollbarComponent } from '~/_shared/components/scrollbar/overlayScrollbar.component';
import { ScrollbarType } from '~/_shared/components/scrollbar/scrollbar.component';
import { useScrollControls } from '~/_shared/components/scrollbar/useScrollControls.hook';
import { useResizeObserver } from '~/_shared/hooks/useResizeObserver';
import { isTouchOnly } from '~/_shared/utils/deviceDetect/deviceDetect.helpers';
import { usePrevious } from '~/_shared/utils/hooks/usePrevious';
import {
  arrowStyle, arrowsWrapperStyle, contentScrollbarStyle, contentStyle, dividerStyle, dividerWrapperStyle,
  fadeBottomStyle, fadeTopStyle, overlappingBackgroundStyle,
  overlappingPanelTabStyle,
  overlappingPanelTabsWrapperStyle, overlappingUIPanelWrapperStyles,
} from '~/map/mapToolControls/menuItems/overlappingUI/overlappingUI.styles';
import type { OverlappingUIPanelTabs } from '~/map/mapToolControls/menuItems/overlappingUI/overlappingUI.types';
import { OverlappingUIPanelItemComponent } from '~/map/mapToolControls/menuItems/overlappingUI/overlappingUIPanelItem.component';

const isTouchOnlyDevice = isTouchOnly();

export type OverlappingUIPanelProps = Readonly<{
  overlappingTabs: readonly OverlappingUIPanelTabs[];
}>;

export const OverlappingUIPanelComponent: FC<OverlappingUIPanelProps> = ({ overlappingTabs }) => {
  const relevantTabs = useMemo(() => {
    return overlappingTabs.filter((tab) => (tab.elements.length > 1));
  }, [overlappingTabs]);
  const previousRelevantTabs = usePrevious(relevantTabs);
  const [activeTabId, setActiveTabId] = useState(relevantTabs[0]?.id);
  const [isHeightAnimationInProgress, setIsHeightAnimationInProgress] = useState(false);
  const isHeightAnimationInProgressRef = useRef(isHeightAnimationInProgress);
  const [showScrollArrows, setShowScrollArrows] = useState(false);
  const animationStartHandler = useRef((event: TransitionEvent) => {
    if ((event.propertyName === 'height') && (
      (event.target as HTMLElement | null)?.matches('[data-overlayscrollbars-initialize]'))
    ) {
      setIsHeightAnimationInProgress(true);
      isHeightAnimationInProgressRef.current = true;
    }
  });
  const animationEndHandler = useRef((event: TransitionEvent) => {
    if ((event.propertyName === 'height') && (
      (event.target as HTMLElement | null)?.matches('[data-overlayscrollbars-initialize]'))
    ) {
      setTimeout(() => {
        // We need a bit of a delay to re-measure stuff before we potentially commit showing/hiding the arrows.
        setIsHeightAnimationInProgress(false);
        isHeightAnimationInProgressRef.current = false;
        updateScrollBoundariesRef.current();
      }, 100);
    }
  });
  const [isScrollableElementMounted, setIsScrollableElementMounted] = useState(false);
  const scrollbarRef = useRef<OverlayScrollbarsComponentRef | null>(null);
  const contentElementRef = useRef<HTMLDivElement | null>(null);
  const scrollableElementRef = useRef<HTMLDivElement | null>(null);
  const {
    atBottom, atTop, scrollDown, scrollUp, stopScrolling, updateScrollBoundaries, scrollYPossible,
  } = useScrollControls(scrollbarRef);
  const updateScrollBoundariesRef = useRef(updateScrollBoundaries);
  const activeTab = relevantTabs.find((tab) => tab.id === activeTabId);

  const stableUpdateScrollBoundaries = useCallback(() => {
    if (!isHeightAnimationInProgressRef.current) {
      updateScrollBoundariesRef.current();
    }
  }, []);

  const scrollableElementRefCallback = useCallback((element: HTMLDivElement) => {
    if (scrollableElementRef.current) {
      scrollableElementRef.current.removeEventListener('transitionstart', animationStartHandler.current);
      scrollableElementRef.current.removeEventListener('transitionend', animationEndHandler.current);
    }

    scrollableElementRef.current = element;
    setIsScrollableElementMounted(!!element);

    if (element) {
      element.addEventListener('transitionstart', animationStartHandler.current);
      element.addEventListener('transitionend', animationEndHandler.current);
    }
  }, []);

  const { height: contentHeight } = useResizeObserver({ ref: contentElementRef.current ? contentElementRef : null });

  useResizeObserver({
    ref: (isScrollableElementMounted && scrollableElementRef.current) ? scrollableElementRef : null,
    onResize: stableUpdateScrollBoundaries,
  });

  useEffect(() => {
    if (!isHeightAnimationInProgress && !isHeightAnimationInProgressRef.current) {
      setShowScrollArrows(scrollYPossible && !isTouchOnlyDevice);
    }
  }, [isHeightAnimationInProgress, scrollYPossible]);

  useEffect(() => {
    if (!activeTab) {
      setActiveTabId(relevantTabs[0]?.id);
    }
  }, [activeTab, relevantTabs]);

  useEffect(() => {
    const oldTabIDs = previousRelevantTabs?.map((tab) => tab.id) ?? [];
    const newTabIDs = relevantTabs.map(tab => tab.id);

    const newlyAddedTabId = newTabIDs.filter(id => !oldTabIDs.includes(id))?.[0];
    if (newlyAddedTabId) {
      setActiveTabId(newlyAddedTabId);
    }
  }, [relevantTabs, previousRelevantTabs]);

  useEffect(() => {
    updateScrollBoundariesRef.current = updateScrollBoundaries;
  }, [updateScrollBoundaries]);

  const handleScrollUp = useCallback(() => {
    if (!atTop) {
      scrollUp();
    }
  }, [atTop, scrollUp]);

  const handleScrollDown = useCallback(() => {
    if (!atBottom) {
      scrollDown();
    }
  }, [atBottom, scrollDown]);

  const overlappingElements = activeTab?.elements;

  useEffect(() => {
    const _ = overlappingElements; // To ensure dependency
    updateScrollBoundariesRef?.current();
  }, [overlappingElements]);

  if (!overlappingElements || (overlappingElements.length < 2)) {
    return null;
  }

  return (
    <div css={overlappingUIPanelWrapperStyles}>
      {(relevantTabs.length > 1) && (
        <div
          data-element="overlapping-panel-tabs-wrapper"
          css={overlappingPanelTabsWrapperStyle}
        >
          {relevantTabs.map((tab) => (
            <TooltipComponent
              key={tab.id}
              tooltipContent={tab.tooltipText}
              placement={TooltipPlacement.Right}
            >
              <button
                data-is-active={tab.id === activeTabId}
                css={overlappingPanelTabStyle}
                onClick={() => setActiveTabId(tab.id)}
              >
                <FontAwesomeIcon icon={tab.icon} />
              </button>
            </TooltipComponent>
          ))}
        </div>
      )}
      <div
        ref={scrollableElementRefCallback}
        css={overlappingBackgroundStyle}
      >
        {showScrollArrows && (
          <>
            <div css={arrowsWrapperStyle}>
              <button
                css={arrowStyle(atTop)}
                onMouseDown={handleScrollUp}
                onMouseUp={stopScrolling}
                onMouseLeave={stopScrolling}
              >
                <FontAwesomeIcon icon={faCaretUp} />
              </button>
              <button
                css={arrowStyle(atBottom)}
                onMouseDown={handleScrollDown}
                onMouseUp={stopScrolling}
                onMouseLeave={stopScrolling}
              >
                <FontAwesomeIcon icon={faCaretDown} />
              </button>
            </div>

            <div css={dividerWrapperStyle}>
              <DividerComponent css={dividerStyle} />
              <div css={!isTouchOnlyDevice && fadeTopStyle(!atTop)} />
            </div>
          </>
        )}

        <div css={fadeTopStyle(isTouchOnlyDevice && !atTop)} />
        <OverlayScrollbarComponent
          isScrollbarHidden
          ref={scrollbarRef}
          onUpdate={updateScrollBoundaries}
          type={ScrollbarType.Vertical}
          css={contentScrollbarStyle}
          style={{ height: contentHeight ? contentHeight + 12 /* contentStyle.padding */ : undefined }}
        >
          <div
            ref={contentElementRef}
            css={contentStyle}
          >
            {overlappingElements.map((item, index) => (
              <OverlappingUIPanelItemComponent
                key={`${index}|${item.name}`}
                item={item}
              />
            ))}
          </div>
        </OverlayScrollbarComponent>
        <div css={fadeBottomStyle(!atBottom)} />
      </div>
    </div>
  );
};
